import { isDevelopment } from '../environment';
import { getRetailer } from '../data/retailer';
import { getUser } from '../data/user';
import { getFeatures } from '../features/getFeatures';
import { getFromStorage, saveToStorage, } from '../preferences/localStorageWrapper';
import { getDatadogUser } from './datadogCommon';
const SAMPLE_RATE = 10;
const PREMIUM_SAMPLE_RATE = 100;
const FEATURE_CACHE_KEY = 'dd_feature_cache';
const ALLOWED_TRACING_ORIGINS = [`https://${window.location.host}`];
const getSessionReplayFeatureKey = (applicationName) => `${applicationName}_session_replay`;
const getAllSessionReplayFeatureKey = (applicationName) => `${applicationName}_all_session_replay`;
const getFeatureCache = () => getFromStorage(FEATURE_CACHE_KEY) || {};
const getFromFeatureCache = (applicationName, featureName) => {
    const features = getFeatureCache();
    return Boolean(features && features[featureName]);
};
const setFeatureForKey = (featureCache, features, key) => {
    const updatedCache = Object.assign({}, featureCache);
    if (key in features) {
        updatedCache[key] = features[key];
    }
    else if (key in updatedCache) {
        delete updatedCache[key];
    }
    return updatedCache;
};
const initFeatureCache = (applicationName) => {
    getFeatures(getSessionReplayFeatureKey(applicationName), getAllSessionReplayFeatureKey(applicationName)).then(features => {
        const featureCache = getFeatureCache();
        const sessionKey = getSessionReplayFeatureKey(applicationName);
        const sessionAllKey = getAllSessionReplayFeatureKey(applicationName);
        let updatedCache = setFeatureForKey(featureCache, features, sessionKey);
        updatedCache = setFeatureForKey(updatedCache, features, sessionAllKey);
        saveToStorage(FEATURE_CACHE_KEY, updatedCache);
    });
};
const shouldSessionReplay = (applicationName) => getFromFeatureCache(applicationName, getSessionReplayFeatureKey(applicationName));
const shouldAllSessionReplay = (applicationName) => getFromFeatureCache(applicationName, getAllSessionReplayFeatureKey(applicationName));
const ddPromise = new Promise((resolve, reject) => {
    if ('DD_RUM' in window) {
        DD_RUM.onReady(() => {
            resolve();
        });
    }
    else {
        reject(new Error('Datadog not set'));
    }
});
ddPromise.catch(() => { });
const getDataDogConfig = ({ applicationName, clientToken, applicationId, }) => {
    var _a, _b;
    const gatherAll = shouldAllSessionReplay(applicationName);
    return {
        clientToken,
        applicationId,
        site: 'us5.datadoghq.com',
        service: applicationName,
        env: 'production',
        sampleRate: gatherAll ? 100 : SAMPLE_RATE,
        premiumSampleRate: gatherAll ? 100 : PREMIUM_SAMPLE_RATE,
        silentMultipleInit: true,
        trackInteractions: true,
        trackFrustrations: true,
        allowedTracingOrigins: ALLOWED_TRACING_ORIGINS,
        defaultPrivacyLevel: 'mask-user-input',
        version: ((_b = (_a = window === null || window === void 0 ? void 0 : window.process) === null || _a === void 0 ? void 0 : _a.env) === null || _b === void 0 ? void 0 : _b.REACT_APP_GIT_SHORT) || '',
    };
};
const runDatadog = ({ applicationName, applicationId, clientToken, }) => {
    DD_RUM.init(getDataDogConfig({ applicationName, clientToken, applicationId }));
};
const setDatadogUser = (userPromise) => userPromise.then(([user, retailer]) => {
    DD_RUM.setUser(getDatadogUser(user, retailer));
});
/**
 * Initialise Datadog monitoring for the application.
 * See /docs/datadog for details
 */
export const initDatadog = ({ applicationName, clientToken, applicationId, authenticated = true, }) => {
    if (isDevelopment(window.location.href)) {
        return;
    }
    let userPromise;
    if (authenticated) {
        initFeatureCache(applicationName);
        userPromise = Promise.all([getUser(), getRetailer()]);
    }
    ddPromise.then(() => {
        runDatadog({ applicationName, clientToken, applicationId });
        if (userPromise) {
            setDatadogUser(userPromise);
        }
        if (shouldSessionReplay(applicationName) ||
            shouldAllSessionReplay(applicationName)) {
            DD_RUM.startSessionReplayRecording();
        }
    });
};
